/**
 * Scenery — sky, grass, roads, obstacles, mood overlays, trees, bushes.
 * Cartoon ToonTown-flavoured: vibrant pop-art skyline, candy clouds.
 */
import { OUTLINE, roundRect } from "./itemVisuals";

const WORLD_W = 1280;
const WORLD_H = 820;

// Vivid cartoon palette for the parallax skyline (Toon Town / pop-art).
const TOON_BLDGS = [
  "#FF3D7F", "#7B2CBF", "#FFC857", "#3A86FF", "#06D6A0",
  "#F26419", "#118AB2", "#EF476F", "#9B5DE5", "#00BBF9",
];

export function drawSky(ctx, theme, skylineImg, camCX = WORLD_W / 2) {
  // Three-layer parallax: each layer partially counteracts the world-camera
  // translate so it appears to move slower than the ground, giving depth.
  //   factor 1.0 = perfectly anchored to camera (doesn't move)
  //   factor 0.0 = moves fully with the world (no parallax)
  // We draw everything wider than WORLD_W by PARALLAX_PAD on each side so
  // partial shifts never reveal a gap on the edges.
  const PARALLAX_PAD = 800;
  const stripH = WORLD_H * 0.22;

  // --- Base sky gradient (moves with camera — factor 1.0) ---
  ctx.save();
  ctx.translate(camCX - WORLD_W / 2, 0);
  const sky = ctx.createLinearGradient(0, 0, 0, stripH);
  sky.addColorStop(0, theme.bg_top);
  sky.addColorStop(1, theme.bg_bottom);
  ctx.fillStyle = sky;
  ctx.fillRect(-PARALLAX_PAD, 0, WORLD_W + PARALLAX_PAD * 2, stripH);
  ctx.restore();

  // --- Far layer: AI pixel-art skyline (factor 0.7) ---
  ctx.save();
  // Apply parallax: shift right by camCX * factor so it lags behind world.
  ctx.translate(camCX * 0.7 - WORLD_W / 2 * 0.7, 0);
  drawFarSkyline(ctx, theme, skylineImg, stripH, PARALLAX_PAD);
  ctx.restore();

  // --- Mid layer: procedural silhouette building row (factor 0.45) ---
  ctx.save();
  ctx.translate(camCX * 0.45 - WORLD_W / 2 * 0.45, 0);
  drawMidSilhouette(ctx, theme, stripH, PARALLAX_PAD);
  ctx.restore();

  // --- Near layer: tiny foreground rooftop fringe (factor 0.15) ---
  ctx.save();
  ctx.translate(camCX * 0.15 - WORLD_W / 2 * 0.15, 0);
  drawNearRooftops(ctx, theme, stripH, PARALLAX_PAD);
  ctx.restore();

  // --- Drifting puffy clouds at the far layer's speed (factor 0.7) ---
  ctx.save();
  ctx.translate(camCX * 0.7 - WORLD_W / 2 * 0.7, 0);
  const t = performance.now() / 40;
  ctx.globalAlpha = 0.85;
  for (let i = 0; i < 6; i++) {
    const x = ((i * 320 + t * 0.6) % (WORLD_W + 400)) - 200;
    const y = 30 + (i % 3) * 22;
    drawCloud(ctx, x, y, i % 2 === 0 ? "#FFFFFF" : "#FFE3F1");
  }
  ctx.restore();
}

// Far layer — the AI-generated pixel-art skyline, or a hand-drawn fallback.
function drawFarSkyline(ctx, theme, skylineImg, stripH, pad) {
  if (skylineImg && skylineImg.complete && skylineImg.naturalWidth > 0) {
    // Tile the image across [-pad, WORLD_W+pad] using `cover` semantics so
    // the parallax offset never shows a gap.
    const ar = skylineImg.naturalWidth / skylineImg.naturalHeight;
    const drawW = WORLD_W + pad * 2;
    let dw = drawW;
    let dh = stripH;
    // Preserve aspect — anchor bottom of image to bottom of strip.
    const targetAR = drawW / stripH;
    if (ar > targetAR) { dh = stripH; dw = stripH * ar; }
    else               { dw = drawW;  dh = drawW / ar; }
    const dx = -pad + (drawW - dw) / 2;
    const dy = stripH - dh;
    ctx.drawImage(skylineImg, dx, dy, dw, dh);
    // Tasteful horizon line
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(-pad, stripH);
    ctx.lineTo(WORLD_W + pad, stripH);
    ctx.stroke();
  } else {
    // Fallback: hand-drawn pop-art skyline (also widened).
    ctx.save();
    const sky = ctx.createLinearGradient(0, 0, 0, WORLD_H * 0.45);
    sky.addColorStop(0, theme.bg_top);
    sky.addColorStop(1, theme.bg_bottom);
    ctx.fillStyle = sky;
    ctx.fillRect(-pad, 0, WORLD_W + pad * 2, stripH);
    drawSkylineLayer(ctx, 0.55, "rgba(20, 16, 40, 0.9)", 0, pad);
    drawSkylineLayer(ctx, 1.0, null, 1, pad);
    ctx.restore();
  }
}

// Mid layer — procedural pixel-art building silhouettes tinted with the
// theme's accent, semi-transparent, offset slightly lower than the far
// horizon so they peek over the AI skyline.
function drawMidSilhouette(ctx, theme, stripH, pad) {
  const baseline = stripH + 6; // a hair below the far horizon
  ctx.save();
  ctx.globalAlpha = 0.55;
  // Darken the theme stripe color for a shadow-silhouette feel.
  ctx.fillStyle = "rgba(12, 14, 20, 0.82)";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  let x = -pad;
  let i = 0;
  while (x < WORLD_W + pad) {
    const seed = (i * 7331 + 13) % 997;
    const w = 24 + (seed % 40);
    const h = 28 + ((seed * 3) % 44);
    ctx.fillRect(x, baseline - h, w, h);
    ctx.strokeRect(x, baseline - h, w, h);
    // Window hint — 2-3 chunky lit windows per building, theme accent.
    ctx.fillStyle = theme.accent_glow || "#FFD166";
    for (let r = 0; r < 2; r++) {
      for (let cc = 0; cc < 2; cc++) {
        if ((seed + r + cc) % 3 === 0) continue;
        ctx.fillRect(x + 4 + cc * (w - 8) - 2, baseline - h + 6 + r * 12, 3, 3);
      }
    }
    ctx.fillStyle = "rgba(12, 14, 20, 0.82)";
    x += w + 2 + (seed % 6);
    i++;
  }
  ctx.restore();
}

// Near layer — small, darker rooftop fringe along the bottom of the sky
// strip. Adds depth cue when the camera pans (this layer moves fastest of
// the three, closest to world-anchored).
function drawNearRooftops(ctx, theme, stripH, pad) {
  const baseline = stripH + 18;
  ctx.save();
  ctx.fillStyle = "rgba(6, 8, 14, 0.92)";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  let x = -pad;
  let i = 0;
  while (x < WORLD_W + pad) {
    const seed = (i * 4409 + 31) % 617;
    const w = 36 + (seed % 26);
    const h = 14 + ((seed * 2) % 22);
    ctx.fillRect(x, baseline - h, w, h);
    ctx.strokeRect(x, baseline - h, w, h);
    // Antenna or water tower on ~1 in 4
    if ((seed >> 2) % 4 === 0) {
      ctx.beginPath();
      ctx.moveTo(x + w / 2, baseline - h);
      ctx.lineTo(x + w / 2, baseline - h - 10);
      ctx.stroke();
    }
    x += w + 1;
    i++;
  }
  ctx.restore();
}

// Draw a row of cartoon skyscrapers anchored at WORLD_H * 0.22 (where grass starts).
// `layer` 0 = far/dim silhouette, `layer` 1 = vivid colourful foreground.
// `pad` extends the draw range beyond [-pad, WORLD_W+pad] for parallax padding.
function drawSkylineLayer(ctx, opacity, override, layer, pad = 0) {
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = layer === 1 ? 3 : 2;
  ctx.lineJoin = "round";
  const baseline = WORLD_H * 0.22 + 2;
  let x = -10 - pad;
  let i = 0;
  while (x < WORLD_W + 20 + pad) {
    // deterministic-but-varied building shape
    const seed = (i * 9301 + 49297) % 233280;
    const w = 36 + ((seed >> 4) % 70);
    const h = 60 + ((seed >> 2) % 130) + (layer === 0 ? 20 : 0);
    const top = baseline - h;
    const color = override || TOON_BLDGS[i % TOON_BLDGS.length];

    // body
    ctx.fillStyle = color;
    ctx.fillRect(x, top, w, h);
    if (layer === 1) ctx.strokeRect(x, top, w, h);

    if (layer === 1) {
      // antenna spire on tall ones
      if ((seed >> 6) % 4 === 0 && h > 110) {
        ctx.strokeStyle = OUTLINE;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x + w / 2, top);
        ctx.lineTo(x + w / 2, top - 16 - ((seed >> 8) % 14));
        ctx.stroke();
      }
      // sloped/peaked roofs
      if ((seed >> 9) % 5 === 0) {
        ctx.fillStyle = TOON_BLDGS[(i + 3) % TOON_BLDGS.length];
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x + w / 2, top - 18);
        ctx.lineTo(x + w, top);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
      // windows grid
      ctx.fillStyle = (i % 3 === 0) ? "#FFD23F" : "#FFFFFF";
      const cols = Math.max(2, Math.floor(w / 10));
      const rows = Math.max(3, Math.floor(h / 14));
      for (let cc = 0; cc < cols; cc++) {
        for (let rr = 0; rr < rows; rr++) {
          // skip a few windows to vary
          if (((cc * 7 + rr * 11 + i) % 5) === 0) continue;
          const wx = x + 4 + cc * (w - 8) / Math.max(1, cols - 1) - 2;
          const wy = top + 8 + rr * (h - 16) / Math.max(1, rows - 1) - 2;
          if (wx + 4 > x + w - 4) continue;
          if (wy + 4 > top + h - 4) continue;
          ctx.fillRect(wx, wy, 4, 4);
        }
      }
      // colour stripe
      if ((seed >> 11) % 3 === 0) {
        ctx.fillStyle = "rgba(0,0,0,0.18)";
        ctx.fillRect(x, top + h * 0.45, w, 4);
      }
    }
    x += w + 2 - (layer === 0 ? 2 : 0);
    i++;
  }
  ctx.restore();
}

function drawCloud(ctx, x, y, fill = "#FFFFFF") {
  // Chunky pixel-art cloud — stepped rectangle blocks instead of round
  // smudgy circles. Reads as 16-bit retro arcade.
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y));
  ctx.fillStyle = fill;
  // Body (base layer)
  ctx.fillRect(-30, -2, 60, 8);
  ctx.fillRect(-24, -8, 48, 6);
  ctx.fillRect(-12, -14, 24, 6);
  ctx.fillRect(-26, 6, 52, 4);
  // Bottom plump
  ctx.fillRect(-34, 2, 6, 4);
  ctx.fillRect(28, 2, 6, 4);
  // Top highlight
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(-10, -12, 20, 2);
  ctx.fillRect(-20, -6, 6, 2);
  // Bottom shadow
  ctx.fillStyle = "rgba(180, 195, 215, 0.6)";
  ctx.fillRect(-26, 8, 50, 2);
  ctx.fillRect(-18, 10, 36, 2);
  ctx.restore();
}

// City-specific ground textures. Each entry is a function that paints the
// ground tiles for a given city. Replaces the generic green grass with
// culturally-themed pixel art (cobblestone for Paris, sand for Cairo,
// terracotta for Mexico, etc.). The 4 PAVEMENT_CITIES still use the gritty
// grey concrete pass.
const PAVEMENT_CITIES = new Set(["nyc", "detroit", "toronto", "la"]);
const GROUND_DRAWERS = {
  paris: drawCobblestone,
  london: drawRedBrickWalk,
  tokyo: drawWetAsphalt,
  cairo: drawSand,
  beirut: drawMediterraneanStone,
  mexico_city: drawTerracotta,
};

export function drawGrass(ctx, items, cityId) {
  if (PAVEMENT_CITIES.has(cityId)) {
    drawPavement(ctx, items, cityId);
    return;
  }
  const drawer = GROUND_DRAWERS[cityId];
  if (drawer) {
    drawer(ctx, items, cityId);
    return;
  }
  // Fallback — vibrant cartoon green ground (legacy)
  const grad = ctx.createLinearGradient(0, WORLD_H * 0.22, 0, WORLD_H);
  grad.addColorStop(0, "#7FE34A");
  grad.addColorStop(1, "#3DAA38");
  ctx.fillStyle = grad;
  ctx.fillRect(0, WORLD_H * 0.22, WORLD_W, WORLD_H);

  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  for (let i = 0; i < 70; i++) {
    const x = (i * 137 + 17) % WORLD_W;
    const y = (i * 83 + 200) % (WORLD_H * 0.78) + WORLD_H * 0.22;
    if ((Math.abs(y - WORLD_H / 2) < 70) || (Math.abs(x - WORLD_W / 2) < 70)) continue;
    let near = false;
    for (const it of items) {
      if (!it.collected && Math.hypot(it.x - x, it.y - y) < 95) { near = true; break; }
    }
    if (near) continue;
    ctx.fillStyle = (x + y) % 2 === 0 ? "#6DB344" : "#9FDD6B";
    ctx.beginPath();
    ctx.moveTo(x - 7, y);
    ctx.quadraticCurveTo(x - 3, y - 12, x, y);
    ctx.quadraticCurveTo(x + 3, y - 14, x + 7, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  for (let i = 0; i < 12; i++) {
    const x = (i * 213 + 60) % (WORLD_W - 80) + 40;
    const y = ((i * 97 + 110) % (WORLD_H * 0.78)) + WORLD_H * 0.22;
    if ((Math.abs(y - WORLD_H / 2) < 80) || (Math.abs(x - WORLD_W / 2) < 80)) continue;
    let near = false;
    for (const it of items) {
      if (!it.collected && Math.hypot(it.x - x, it.y - y) < 110) { near = true; break; }
    }
    if (near) continue;
    if (i % 3 === 0) drawTree(ctx, x, y);
    else drawBush(ctx, x, y);
  }
}

// --- City-specific ground drawers ------------------------------------------

// Paris — rounded cobblestone tiles in offset rows. Light-gray w/ darker
// grout lines. Picture: rue de Montmartre.
function drawCobblestone(ctx, items, _cityId) {
  // Base
  const grad = ctx.createLinearGradient(0, WORLD_H * 0.22, 0, WORLD_H);
  grad.addColorStop(0, "#A89F94");
  grad.addColorStop(1, "#7C7468");
  ctx.fillStyle = grad;
  ctx.fillRect(0, WORLD_H * 0.22, WORLD_W, WORLD_H);
  // Cobble tiles — staggered rows of ovals
  const cw = 24, ch = 14;
  const startY = Math.floor(WORLD_H * 0.22 / ch) * ch;
  ctx.strokeStyle = "rgba(40,35,30,0.55)";
  ctx.lineWidth = 1;
  for (let y = startY; y < WORLD_H; y += ch) {
    const rowOff = (Math.floor(y / ch) % 2) * (cw / 2);
    for (let x = -cw + rowOff; x < WORLD_W; x += cw) {
      const seed = ((x * 131) ^ (y * 197)) >>> 0;
      const tone = ["#9C9387", "#A89F94", "#B6AC9F", "#857C70"][seed % 4];
      ctx.fillStyle = tone;
      // Rounded cobble — fill + 1px grout border
      ctx.fillRect(x + 1, y + 1, cw - 2, ch - 2);
      // soft highlight
      ctx.fillStyle = "rgba(255,250,240,0.18)";
      ctx.fillRect(x + 2, y + 1, cw - 4, 2);
      // grout
      ctx.strokeRect(x + 0.5, y + 0.5, cw - 1, ch - 1);
    }
  }
  _scatterPropsAvoidRoad(ctx, items, _cityId, "cobble");
}

// London — small red bricks in classic running-bond pattern.
function drawRedBrickWalk(ctx, items, _cityId) {
  const grad = ctx.createLinearGradient(0, WORLD_H * 0.22, 0, WORLD_H);
  grad.addColorStop(0, "#A14B3D");
  grad.addColorStop(1, "#7A3328");
  ctx.fillStyle = grad;
  ctx.fillRect(0, WORLD_H * 0.22, WORLD_W, WORLD_H);
  const bw = 28, bh = 10;
  const startY = Math.floor(WORLD_H * 0.22 / bh) * bh;
  ctx.strokeStyle = "rgba(20,12,8,0.5)";
  ctx.lineWidth = 1;
  for (let y = startY; y < WORLD_H; y += bh) {
    const rowOff = (Math.floor(y / bh) % 2) * (bw / 2);
    for (let x = -bw + rowOff; x < WORLD_W; x += bw) {
      const seed = ((x * 73) ^ (y * 109)) >>> 0;
      const tone = ["#A14B3D", "#8B3F33", "#B5564A", "#933A2E"][seed % 4];
      ctx.fillStyle = tone;
      ctx.fillRect(x + 1, y + 1, bw - 2, bh - 2);
      // Brick highlight strip
      ctx.fillStyle = "rgba(255,200,180,0.12)";
      ctx.fillRect(x + 2, y + 1, bw - 4, 1);
      // Mortar
      ctx.strokeRect(x + 0.5, y + 0.5, bw - 1, bh - 1);
    }
  }
  _scatterPropsAvoidRoad(ctx, items, _cityId, "brick");
}

// Tokyo — wet asphalt with neon pink/cyan reflections (Shibuya at night).
function drawWetAsphalt(ctx, items, _cityId) {
  const grad = ctx.createLinearGradient(0, WORLD_H * 0.22, 0, WORLD_H);
  grad.addColorStop(0, "#1A1B22");
  grad.addColorStop(1, "#0E0F14");
  ctx.fillStyle = grad;
  ctx.fillRect(0, WORLD_H * 0.22, WORLD_W, WORLD_H);
  // Speckled wet shine — random pink/cyan dots reflecting the neon billboards.
  const tile = 8;
  const startY = Math.floor(WORLD_H * 0.22 / tile) * tile;
  for (let y = startY; y < WORLD_H; y += tile) {
    for (let x = 0; x < WORLD_W; x += tile) {
      const seed = (x * 73856093) ^ (y * 19349663);
      const n = (seed >>> 0) % 50;
      if (n === 0) {
        // Pink neon puddle reflection
        ctx.fillStyle = "rgba(255, 61, 248, 0.30)";
        ctx.fillRect(x, y, tile, tile);
      } else if (n === 1) {
        // Cyan neon puddle reflection
        ctx.fillStyle = "rgba(0, 240, 255, 0.25)";
        ctx.fillRect(x, y, tile, tile);
      } else if (n < 6) {
        // Subtle highlight
        ctx.fillStyle = "rgba(255, 255, 255, 0.03)";
        ctx.fillRect(x, y, tile, tile);
      }
    }
  }
  // Rain streaks (vertical lines)
  ctx.strokeStyle = "rgba(180, 210, 255, 0.10)";
  ctx.lineWidth = 1;
  for (let i = 0; i < 30; i++) {
    const sx = (i * 137 + 13) % WORLD_W;
    const sy = WORLD_H * 0.22 + ((i * 53) % (WORLD_H * 0.78));
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(sx, sy + 12);
    ctx.stroke();
  }
}

// Cairo — warm sand with darker speckles + occasional dune ripple.
function drawSand(ctx, items, _cityId) {
  const grad = ctx.createLinearGradient(0, WORLD_H * 0.22, 0, WORLD_H);
  grad.addColorStop(0, "#E8C987");
  grad.addColorStop(1, "#C9A05C");
  ctx.fillStyle = grad;
  ctx.fillRect(0, WORLD_H * 0.22, WORLD_W, WORLD_H);
  // Sand grain speckles
  const tile = 6;
  const startY = Math.floor(WORLD_H * 0.22 / tile) * tile;
  for (let y = startY; y < WORLD_H; y += tile) {
    for (let x = 0; x < WORLD_W; x += tile) {
      const seed = (x * 73856093) ^ (y * 19349663);
      const n = (seed >>> 0) % 25;
      if (n < 4) {
        ctx.fillStyle = "rgba(120, 86, 50, 0.22)";
        ctx.fillRect(x, y, tile, tile);
      } else if (n === 4) {
        ctx.fillStyle = "rgba(255, 240, 200, 0.20)";
        ctx.fillRect(x, y, tile, tile);
      }
    }
  }
  // Dune ripple curves — wavy horizontal lines.
  ctx.strokeStyle = "rgba(120, 86, 50, 0.30)";
  ctx.lineWidth = 1;
  for (let i = 0; i < 8; i++) {
    const sy = WORLD_H * 0.22 + 80 + i * 80;
    if (Math.abs(sy - WORLD_H / 2) < 60) continue;
    ctx.beginPath();
    for (let x = 0; x <= WORLD_W; x += 8) {
      const dy = Math.sin((x + i * 100) / 80) * 3;
      if (x === 0) ctx.moveTo(x, sy + dy);
      else ctx.lineTo(x, sy + dy);
    }
    ctx.stroke();
  }
  _scatterPropsAvoidRoad(ctx, items, _cityId, "sand");
}

// Beirut — Mediterranean cream-stone tiles, larger square cuts.
function drawMediterraneanStone(ctx, items, _cityId) {
  const grad = ctx.createLinearGradient(0, WORLD_H * 0.22, 0, WORLD_H);
  grad.addColorStop(0, "#E8DBC0");
  grad.addColorStop(1, "#C4B393");
  ctx.fillStyle = grad;
  ctx.fillRect(0, WORLD_H * 0.22, WORLD_W, WORLD_H);
  const sw = 32, sh = 22;
  const startY = Math.floor(WORLD_H * 0.22 / sh) * sh;
  ctx.strokeStyle = "rgba(70, 55, 35, 0.45)";
  ctx.lineWidth = 1;
  for (let y = startY; y < WORLD_H; y += sh) {
    const rowOff = (Math.floor(y / sh) % 3) * (sw / 3);
    for (let x = -sw + rowOff; x < WORLD_W; x += sw) {
      const seed = ((x * 89) ^ (y * 137)) >>> 0;
      const tone = ["#E8DBC0", "#D5C4A4", "#EFE3CB", "#C4B393"][seed % 4];
      ctx.fillStyle = tone;
      ctx.fillRect(x + 1, y + 1, sw - 2, sh - 2);
      ctx.fillStyle = "rgba(255,250,240,0.10)";
      ctx.fillRect(x + 2, y + 1, sw - 4, 2);
      ctx.strokeRect(x + 0.5, y + 0.5, sw - 1, sh - 1);
    }
  }
  _scatterPropsAvoidRoad(ctx, items, _cityId, "stone");
}

// Mexico City — terracotta diamond tiles.
function drawTerracotta(ctx, items, _cityId) {
  const grad = ctx.createLinearGradient(0, WORLD_H * 0.22, 0, WORLD_H);
  grad.addColorStop(0, "#D26F3A");
  grad.addColorStop(1, "#A04D26");
  ctx.fillStyle = grad;
  ctx.fillRect(0, WORLD_H * 0.22, WORLD_W, WORLD_H);
  // Diamond pattern — alternating colors in a checkerboard at 45°.
  const tw = 28, th = 14;
  const startY = Math.floor(WORLD_H * 0.22 / th) * th;
  for (let y = startY; y < WORLD_H; y += th) {
    const rowOff = (Math.floor(y / th) % 2) * (tw / 2);
    for (let x = -tw + rowOff; x < WORLD_W; x += tw) {
      const seed = ((x * 109) ^ (y * 167)) >>> 0;
      const tone = ["#D26F3A", "#B0552A", "#E58247", "#9A4521"][seed % 4];
      ctx.fillStyle = tone;
      // Diamond — center fat, corners cut
      ctx.beginPath();
      ctx.moveTo(x + tw / 2, y);
      ctx.lineTo(x + tw, y + th / 2);
      ctx.lineTo(x + tw / 2, y + th);
      ctx.lineTo(x, y + th / 2);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "rgba(40,20,8,0.45)";
      ctx.lineWidth = 1;
      ctx.stroke();
      // White grout dot at center for a tile-pattern accent
      if (seed % 7 === 0) {
        ctx.fillStyle = "#F0E6D2";
        ctx.fillRect(x + tw / 2 - 1, y + th / 2 - 1, 2, 2);
      }
    }
  }
  _scatterPropsAvoidRoad(ctx, items, _cityId, "terracotta");
}

// Helper — drop a sparse set of trees / bushes / palms / cacti as ground
// decoration, avoiding the road cross + nearby active items. Used by all
// non-pavement ground textures so the world doesn't feel empty.
function _scatterPropsAvoidRoad(ctx, items, cityId, theme) {
  const palms = cityId === "cairo" || cityId === "beirut";
  for (let i = 0; i < 10; i++) {
    const x = (i * 213 + 60) % (WORLD_W - 80) + 40;
    const y = ((i * 97 + 110) % (WORLD_H * 0.78)) + WORLD_H * 0.22;
    if ((Math.abs(y - WORLD_H / 2) < 80) || (Math.abs(x - WORLD_W / 2) < 80)) continue;
    let near = false;
    for (const it of items) {
      if (!it.collected && Math.hypot(it.x - x, it.y - y) < 110) { near = true; break; }
    }
    if (near) continue;
    if (palms) {
      // Cairo / Beirut — date palms only (no trees/bushes)
      if (i % 2 === 0) drawTree(ctx, x, y);
    } else if (theme === "terracotta" || theme === "cobble" || theme === "stone" || theme === "brick") {
      // Brick / cobble / stone / terracotta — mostly bushes (no trees)
      if (i % 3 === 0) drawBush(ctx, x, y);
    }
  }
}

// Pixel-art grey pavement for gritty-city maps (NYC, Detroit, Toronto, LA).
// Uses chunky 8×8 tiles with varied greys to sell the retro concrete look,
// plus sparse manhole covers + sidewalk cracks as decorative tufts.
function drawPavement(ctx, items, cityId) {
  // Base gradient — subtle top-to-bottom lighting, colder for NYC/Detroit/Toronto,
  // warmer-taupe for LA (sun-bleached SoCal asphalt).
  const grad = ctx.createLinearGradient(0, WORLD_H * 0.22, 0, WORLD_H);
  if (cityId === "la") {
    grad.addColorStop(0, "#9C958A");
    grad.addColorStop(1, "#6F685D");
  } else {
    grad.addColorStop(0, "#7A7E82");
    grad.addColorStop(1, "#4E5156");
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, WORLD_H * 0.22, WORLD_W, WORLD_H);

  // Chunky 8×8 pavement tiles with deterministic noise — reads as pixel-art
  // concrete against the player's crisp pixelated camera.
  const tile = 8;
  const startY = Math.floor(WORLD_H * 0.22 / tile) * tile;
  for (let y = startY; y < WORLD_H; y += tile) {
    for (let x = 0; x < WORLD_W; x += tile) {
      const seed = (x * 73856093) ^ (y * 19349663);
      const n = (seed >>> 0) % 17;
      if (n < 3) {
        // darker speckle
        ctx.fillStyle = cityId === "la" ? "rgba(40,30,20,0.18)" : "rgba(20,20,25,0.22)";
        ctx.fillRect(x, y, tile, tile);
      } else if (n === 4) {
        // lighter highlight
        ctx.fillStyle = cityId === "la" ? "rgba(255,240,210,0.12)" : "rgba(230,235,240,0.10)";
        ctx.fillRect(x, y, tile, tile);
      }
    }
  }

  // Subtle horizontal slab seams every ~80px to break up the flat field.
  ctx.strokeStyle = "rgba(0,0,0,0.22)";
  ctx.lineWidth = 1;
  for (let y = startY + 80; y < WORLD_H; y += 80) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(WORLD_W, y);
    ctx.stroke();
  }

  // Decorative manhole covers + crack marks, placed like the old tufts but
  // skipping the road cross and active items.
  for (let i = 0; i < 22; i++) {
    const x = (i * 137 + 31) % (WORLD_W - 40) + 20;
    const y = (i * 83 + 200) % (WORLD_H * 0.78) + WORLD_H * 0.22;
    if ((Math.abs(y - WORLD_H / 2) < 70) || (Math.abs(x - WORLD_W / 2) < 70)) continue;
    let near = false;
    for (const it of items) {
      if (!it.collected && Math.hypot(it.x - x, it.y - y) < 95) { near = true; break; }
    }
    if (near) continue;
    if (i % 3 === 0) drawManhole(ctx, x, y);
    else drawPavementCrack(ctx, x, y, i);
  }
}

function drawManhole(ctx, x, y) {
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  // rim
  ctx.fillStyle = "#2D2F33";
  ctx.beginPath();
  ctx.arc(0, 0, 11, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // inner cover
  ctx.fillStyle = "#434649";
  ctx.beginPath();
  ctx.arc(0, 0, 8, 0, Math.PI * 2);
  ctx.fill();
  // pixel cross on cover
  ctx.fillStyle = "#1E1F22";
  ctx.fillRect(-6, -1, 12, 2);
  ctx.fillRect(-1, -6, 2, 12);
  ctx.restore();
}

function drawPavementCrack(ctx, x, y, i) {
  ctx.save();
  ctx.strokeStyle = "rgba(20,20,25,0.55)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x - 6, y);
  ctx.lineTo(x - 2, y + (i % 2 === 0 ? -3 : 3));
  ctx.lineTo(x + 3, y + (i % 2 === 0 ? 2 : -2));
  ctx.lineTo(x + 7, y + 1);
  ctx.stroke();
  ctx.restore();
}

function drawTree(ctx, x, y) {
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;
  // trunk
  ctx.fillStyle = "#8B4513";
  ctx.fillRect(-5, 0, 10, 16);
  ctx.strokeRect(-5, 0, 10, 16);
  // 3 puffs of foliage — random cartoon palette
  const palettes = [
    ["#34C759", "#a8f068"],
    ["#FF9F1C", "#FFD23F"],
    ["#FF3D7F", "#FF85A1"],
    ["#06D6A0", "#7FFFB1"],
  ];
  const idx = Math.abs((x * 13 + y * 7) | 0) % palettes.length;
  const [main, hl] = palettes[idx];
  ctx.fillStyle = main;
  ctx.beginPath();
  ctx.arc(-10, -8, 14, 0, Math.PI * 2);
  ctx.arc(0, -18, 16, 0, Math.PI * 2);
  ctx.arc(10, -8, 14, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // highlight
  ctx.fillStyle = hl;
  ctx.beginPath();
  ctx.arc(-4, -20, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawBush(ctx, x, y) {
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;
  ctx.fillStyle = "#43A047";
  ctx.beginPath();
  ctx.arc(-8, 0, 9, 0, Math.PI * 2);
  ctx.arc(0, -4, 11, 0, Math.PI * 2);
  ctx.arc(8, 0, 9, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

// Each city gets a distinct track layout. The layout is purely cosmetic in
// terms of the road painting — physics/obstacles still use `city.obstacles`.
// Layouts: "grid" (cross streets), "oval" (racing oval around the landmark),
// "figure8" (crossed loop), "serpentine" (S-curves), "speedway" (straight with
// checker gates). Each takes (ctx, theme) and draws on top of the grass.
const TRACK_BY_CITY = {
  paris: "figure8",
  nyc: "grid",
  tokyo: "speedway",
  cairo: "serpentine",
  london: "grid",
  la: "speedway",
  beirut: "oval",
  mexico_city: "oval",
  toronto: "grid",
  detroit: "speedway",
};

export function drawRoads(ctx, theme, cityId) {
  const layout = TRACK_BY_CITY[cityId] || "grid";
  if (layout === "oval")       return drawOvalTrack(ctx, theme);
  if (layout === "figure8")    return drawFigure8Track(ctx, theme);
  if (layout === "serpentine") return drawSerpentineTrack(ctx, theme);
  if (layout === "speedway")   return drawSpeedwayTrack(ctx, theme);
  return drawGridTrack(ctx, theme);
}

// ========== GRID — classic cross streets (Manhattan / London / Toronto) ==========
function drawGridTrack(ctx, theme) {
  ctx.fillStyle = theme.road;
  ctx.fillRect(0, WORLD_H / 2 - 48, WORLD_W, 96);
  ctx.fillRect(WORLD_W / 2 - 48, 0, 96, WORLD_H);
  // curbs
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(0, WORLD_H / 2 - 48); ctx.lineTo(WORLD_W, WORLD_H / 2 - 48);
  ctx.moveTo(0, WORLD_H / 2 + 48); ctx.lineTo(WORLD_W, WORLD_H / 2 + 48);
  ctx.moveTo(WORLD_W / 2 - 48, 0); ctx.lineTo(WORLD_W / 2 - 48, WORLD_H);
  ctx.moveTo(WORLD_W / 2 + 48, 0); ctx.lineTo(WORLD_W / 2 + 48, WORLD_H);
  ctx.stroke();
  // stripes
  ctx.fillStyle = theme.stripe;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  for (let x = 20; x < WORLD_W; x += 64) {
    if (x > WORLD_W / 2 - 60 && x < WORLD_W / 2 + 20) continue;
    ctx.fillRect(x, WORLD_H / 2 - 5, 38, 10);
    ctx.strokeRect(x, WORLD_H / 2 - 5, 38, 10);
  }
  for (let y = 20; y < WORLD_H; y += 64) {
    if (y > WORLD_H / 2 - 60 && y < WORLD_H / 2 + 20) continue;
    ctx.fillRect(WORLD_W / 2 - 5, y, 10, 38);
    ctx.strokeRect(WORLD_W / 2 - 5, y, 10, 38);
  }
  // crosswalks
  ctx.fillStyle = "#FFFFFF";
  for (let i = 0; i < 8; i++) {
    ctx.fillRect(WORLD_W / 2 - 44 + i * 12, WORLD_H / 2 - 48 - 26, 8, 22);
    ctx.strokeRect(WORLD_W / 2 - 44 + i * 12, WORLD_H / 2 - 48 - 26, 8, 22);
    ctx.fillRect(WORLD_W / 2 - 44 + i * 12, WORLD_H / 2 + 48 + 4, 8, 22);
    ctx.strokeRect(WORLD_W / 2 - 44 + i * 12, WORLD_H / 2 + 48 + 4, 8, 22);
  }
}

// ========== OVAL — racing loop around the landmark ==========
function drawOvalTrack(ctx, theme) {
  const cx = WORLD_W / 2, cy = WORLD_H / 2 + 60;
  const rx = 520, ry = 240;
  // outer asphalt
  ctx.fillStyle = theme.road;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  // inner grass cutout
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx - 100, ry - 56, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";
  // curbs (outer + inner)
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx - 100, ry - 56, 0, 0, Math.PI * 2);
  ctx.stroke();
  // red/white curb stripes outer edge
  const steps = 40;
  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    const x = cx + Math.cos(a) * rx;
    const y = cy + Math.sin(a) * ry;
    ctx.fillStyle = i % 2 === 0 ? "#FFFFFF" : "#E31C1C";
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fill();
  }
  // start/finish line at left
  drawCheckerBand(ctx, cx - rx - 2, cy - 40, 24, 80);
}

// ========== FIGURE-8 — two loops crossed (Paris around Eiffel) ==========
function drawFigure8Track(ctx, theme) {
  const cxL = WORLD_W * 0.30, cxR = WORLD_W * 0.70, cy = WORLD_H * 0.58;
  const r = 220;
  ctx.fillStyle = theme.road;
  // two fat rings
  for (const cx of [cxL, cxR]) {
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Cut out inner circles
  ctx.globalCompositeOperation = "destination-out";
  for (const cx of [cxL, cxR]) {
    ctx.beginPath();
    ctx.arc(cx, cy, r - 72, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalCompositeOperation = "source-over";
  // curbs
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 6;
  for (const cx of [cxL, cxR]) {
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, r - 72, 0, Math.PI * 2); ctx.stroke();
  }
  // Crossing bridge bar (decorative)
  ctx.fillStyle = "#FFDC9E";
  ctx.fillRect((cxL + cxR) / 2 - 50, cy - 36, 100, 8);
  ctx.strokeRect((cxL + cxR) / 2 - 50, cy - 36, 100, 8);
  // Checker flag at start
  drawCheckerBand(ctx, cxL - r + 10, cy - 30, 20, 60);
}

// ========== SERPENTINE — wide S-curve road (Cairo) ==========
function drawSerpentineTrack(ctx, theme) {
  const amp = 140; // wave amplitude
  const step = 20;
  ctx.fillStyle = theme.road;
  ctx.beginPath();
  ctx.moveTo(0, WORLD_H / 2 + amp);
  for (let x = 0; x <= WORLD_W; x += step) {
    const y = WORLD_H / 2 + Math.sin(x / 180) * amp;
    ctx.lineTo(x, y - 60);
  }
  for (let x = WORLD_W; x >= 0; x -= step) {
    const y = WORLD_H / 2 + Math.sin(x / 180) * amp;
    ctx.lineTo(x, y + 60);
  }
  ctx.closePath();
  ctx.fill();
  // curb edges
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 6;
  ctx.beginPath();
  for (let x = 0; x <= WORLD_W; x += step) {
    const y = WORLD_H / 2 + Math.sin(x / 180) * amp - 60;
    x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.beginPath();
  for (let x = 0; x <= WORLD_W; x += step) {
    const y = WORLD_H / 2 + Math.sin(x / 180) * amp + 60;
    x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.stroke();
  // dashed center stripe following the curve
  ctx.strokeStyle = theme.stripe;
  ctx.lineWidth = 6;
  ctx.setLineDash([26, 20]);
  ctx.beginPath();
  for (let x = 0; x <= WORLD_W; x += step) {
    const y = WORLD_H / 2 + Math.sin(x / 180) * amp;
    x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.setLineDash([]);
}

// ========== SPEEDWAY — wide straight highway with checker speed-gates (Sonic) ==========
function drawSpeedwayTrack(ctx, theme) {
  // Two wide horizontal speedway lanes
  const lane1 = WORLD_H * 0.38;
  const lane2 = WORLD_H * 0.70;
  const laneH = 90;
  ctx.fillStyle = theme.road;
  ctx.fillRect(0, lane1 - laneH / 2, WORLD_W, laneH);
  ctx.fillRect(0, lane2 - laneH / 2, WORLD_W, laneH);
  // curbs
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 6;
  for (const y of [lane1 - laneH / 2, lane1 + laneH / 2, lane2 - laneH / 2, lane2 + laneH / 2]) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(WORLD_W, y); ctx.stroke();
  }
  // Speed-gate checkers across the road every 260px
  for (const laneY of [lane1, lane2]) {
    for (let x = 120; x < WORLD_W; x += 260) {
      drawCheckerBand(ctx, x, laneY - laneH / 2 + 4, 16, laneH - 8);
    }
  }
  // Yellow center dashes
  ctx.fillStyle = theme.stripe;
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 2;
  for (const laneY of [lane1, lane2]) {
    for (let x = 30; x < WORLD_W; x += 70) {
      ctx.fillRect(x, laneY - 4, 40, 8);
      ctx.strokeRect(x, laneY - 4, 40, 8);
    }
  }
}

// Decorative checkered band used by multiple tracks.
function drawCheckerBand(ctx, x, y, w, h) {
  const cells = 4;
  const cw = w / 2;
  const ch = h / cells;
  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < 2; c++) {
      ctx.fillStyle = (r + c) % 2 === 0 ? "#FFFFFF" : "#111111";
      ctx.fillRect(x + c * cw, y + r * ch, cw, ch);
    }
  }
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
}

export function drawObstacle(ctx, o) {
  ctx.save();
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;
  ctx.lineJoin = "round";

  // shadow below
  ctx.fillStyle = "rgba(0,0,0,0.22)";
  ctx.fillRect(o.x + 3, o.y + o.h + 2, o.w, 4);

  if (o.type === "construction") {
    // barricade base
    ctx.fillStyle = "#FFA300";
    ctx.fillRect(o.x, o.y, o.w, o.h);
    ctx.strokeRect(o.x, o.y, o.w, o.h);
    ctx.fillStyle = OUTLINE;
    for (let i = 0; i < o.w; i += 16) ctx.fillRect(o.x + i, o.y, 8, o.h);
    // WORKS sign
    ctx.fillStyle = "#FFD166";
    ctx.fillRect(o.x + o.w / 2 - 20, o.y - 18, 40, 16);
    ctx.strokeRect(o.x + o.w / 2 - 20, o.y - 18, 40, 16);
    ctx.fillStyle = OUTLINE;
    ctx.font = "bold 10px 'Anton'";
    ctx.textAlign = "center";
    ctx.fillText("WORKS", o.x + o.w / 2, o.y - 7);
    // blinking light
    const flash = Math.floor(performance.now() / 350) % 2 === 0;
    ctx.fillStyle = flash ? "#FFD166" : "#E63946";
    ctx.strokeStyle = OUTLINE;
    ctx.beginPath();
    ctx.arc(o.x + o.w - 8, o.y - 6, 4, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
  } else if (o.type === "sewer") {
    const cx = o.x + o.w / 2, cy = o.y + o.h / 2;
    // shadow + rim
    ctx.fillStyle = "#252525";
    ctx.beginPath();
    ctx.arc(cx, cy, o.w / 2 + 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // inner
    ctx.fillStyle = "#555";
    ctx.beginPath();
    ctx.arc(cx, cy, o.w / 2 - 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // grate lines
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 3;
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(cx - o.w / 2 + 10, cy + i * 8);
      ctx.lineTo(cx + o.w / 2 - 10, cy + i * 8);
      ctx.stroke();
    }
  } else if (o.type === "museum") {
    // base
    ctx.fillStyle = "#EFDAB0";
    ctx.fillRect(o.x, o.y + 8, o.w, o.h - 8);
    ctx.strokeRect(o.x, o.y + 8, o.w, o.h - 8);
    // roof pediment
    ctx.fillStyle = "#D9B878";
    ctx.beginPath();
    ctx.moveTo(o.x - 8, o.y + 12);
    ctx.lineTo(o.x + o.w / 2, o.y - 18);
    ctx.lineTo(o.x + o.w + 8, o.y + 12);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // columns
    ctx.fillStyle = "#FFFFFF";
    const cols = 4;
    const colW = 7;
    const gap = (o.w - cols * colW) / (cols + 1);
    for (let i = 0; i < cols; i++) {
      const x = o.x + gap + i * (colW + gap);
      ctx.fillRect(x, o.y + 18, colW, o.h - 26);
      ctx.strokeRect(x, o.y + 18, colW, o.h - 26);
    }
    // MUSEUM sign
    ctx.fillStyle = "#FFD166";
    ctx.fillRect(o.x + o.w / 2 - 28, o.y - 8, 56, 14);
    ctx.strokeRect(o.x + o.w / 2 - 28, o.y - 8, 56, 14);
    ctx.fillStyle = OUTLINE;
    ctx.font = "bold 10px 'Anton'";
    ctx.textAlign = "center";
    ctx.fillText("MUSEUM", o.x + o.w / 2, o.y + 2);
  }
  ctx.restore();
}

export function drawMood(ctx, theme) {
  switch (theme.mood) {
    case "rain": {
      ctx.save();
      ctx.strokeStyle = "rgba(60, 100, 140, 0.35)";
      ctx.lineWidth = 1.5;
      const t = performance.now() / 18;
      for (let i = 0; i < 140; i++) {
        const x = (i * 71 + t) % WORLD_W;
        const y = (i * 37 + t * 3) % WORLD_H;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 4, y + 18);
        ctx.stroke();
      }
      ctx.restore();
      break;
    }
    case "fog": {
      // Rolling London fog — lower base opacity so the city is still
      // readable, with three drifting fog banks that scroll horizontally
      // at different speeds to give a living, atmospheric feel.
      ctx.save();
      const now = performance.now();
      // Light global haze
      ctx.fillStyle = "rgba(230, 234, 240, 0.12)";
      ctx.fillRect(0, 0, WORLD_W, WORLD_H);
      // Three horizontal fog banks at different y bands + drift speeds.
      const banks = [
        { y: WORLD_H * 0.15, h: 120, speed: 0.06, alpha: 0.22, offset: 0 },
        { y: WORLD_H * 0.48, h: 180, speed: 0.10, alpha: 0.18, offset: 170 },
        { y: WORLD_H * 0.78, h: 150, speed: 0.08, alpha: 0.20, offset: 340 },
      ];
      for (const b of banks) {
        ctx.fillStyle = `rgba(238, 242, 246, ${b.alpha})`;
        // Each bank is a series of overlapping ovals that drift; wrap across
        // the world so the fog never "runs out".
        const step = 220;
        const drift = (now * b.speed + b.offset) % step;
        for (let x = -drift; x < WORLD_W + step; x += step) {
          // Oval shaped bank — fake with two stacked rects + soft edges via
          // smaller inset alpha rects, keeping the pixel-art look.
          const cx = x + step / 2;
          const hw = step * 0.8;
          // Main body
          ctx.fillStyle = `rgba(238, 242, 246, ${b.alpha})`;
          ctx.fillRect(cx - hw / 2, b.y, hw, b.h);
          // Softer top / bottom fringes — a couple of thinner bands
          ctx.fillStyle = `rgba(238, 242, 246, ${b.alpha * 0.55})`;
          ctx.fillRect(cx - hw / 2 - 20, b.y - 16, hw + 40, 16);
          ctx.fillRect(cx - hw / 2 - 20, b.y + b.h, hw + 40, 16);
        }
      }
      ctx.restore();
      break;
    }
    case "snow": {
      ctx.save();
      ctx.fillStyle = "#FFFFFF";
      ctx.strokeStyle = OUTLINE;
      ctx.lineWidth = 1;
      const t = performance.now() / 40;
      for (let i = 0; i < 70; i++) {
        const x = (i * 93 + t * 0.7) % WORLD_W;
        const y = (i * 53 + t) % WORLD_H;
        ctx.beginPath();
        ctx.arc(x, y, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      ctx.restore();
      break;
    }
    case "desert": {
      ctx.fillStyle = "rgba(230, 180, 90, 0.22)";
      ctx.fillRect(0, 0, WORLD_W, WORLD_H);
      break;
    }
    case "sunset": {
      const g = ctx.createLinearGradient(0, 0, 0, WORLD_H / 2);
      g.addColorStop(0, "rgba(255, 150, 80, 0.22)");
      g.addColorStop(1, "rgba(255, 150, 80, 0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, WORLD_W, WORLD_H / 2);
      break;
    }
    case "festive": {
      ctx.save();
      ctx.globalAlpha = 0.75;
      ctx.strokeStyle = OUTLINE;
      ctx.lineWidth = 1.5;
      const colors = ["#E63946", "#F5B71B", "#2E9D5E", "#FF1493"];
      const t = performance.now() / 50;
      for (let i = 0; i < 50; i++) {
        const x = (i * 137 + t) % WORLD_W;
        const y = (i * 79 + t * 1.3) % WORLD_H;
        ctx.fillStyle = colors[i % 4];
        ctx.fillRect(x, y, 6, 6);
        ctx.strokeRect(x, y, 6, 6);
      }
      ctx.restore();
      break;
    }
    case "neon": {
      ctx.save();
      ctx.globalAlpha = 0.22;
      ctx.fillStyle = theme.accent_glow;
      for (let i = 0; i < 5; i++) {
        const y = (performance.now() / 40 + i * 160) % WORLD_H;
        ctx.fillRect(0, y, WORLD_W, 3);
      }
      ctx.restore();
      break;
    }
    case "mediterranean": {
      ctx.fillStyle = "rgba(30,144,255,0.1)";
      ctx.fillRect(0, 0, WORLD_W, WORLD_H);
      break;
    }
    case "industrial": {
      ctx.fillStyle = "rgba(110, 80, 50, 0.12)";
      ctx.fillRect(0, 0, WORLD_W, WORLD_H);
      break;
    }
    default: break;
  }
}

export function drawGraceBanner(ctx, secondsLeft) {
  ctx.save();
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(0, WORLD_H / 2 - 60, WORLD_W, 120);
  ctx.font = "bold 72px 'Anton'";
  ctx.textAlign = "center";
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 10;
  ctx.strokeText(`GO IN ${secondsLeft}`, WORLD_W / 2, WORLD_H / 2 + 10);
  ctx.fillStyle = "#FFD166";
  ctx.fillText(`GO IN ${secondsLeft}`, WORLD_W / 2, WORLD_H / 2 + 10);
  ctx.font = "bold 16px 'IBM Plex Sans'";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 4;
  ctx.strokeText("Cops are frozen. Position yourself.", WORLD_W / 2, WORLD_H / 2 + 44);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText("Cops are frozen. Position yourself.", WORLD_W / 2, WORLD_H / 2 + 44);
  ctx.restore();
}

export function drawNameTag(ctx, cx, cy, text) {
  ctx.save();
  ctx.font = "bold 11px 'Anton'";
  const w = ctx.measureText(text).width + 12;
  ctx.fillStyle = "#FFFFFF";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  roundRect(ctx, cx - w / 2, cy - 8, w, 15, 3);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = OUTLINE;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, cx, cy);
  ctx.restore();
}

export function drawHpBar(ctx, cx, cy, frac) {
  ctx.save();
  ctx.fillStyle = "rgba(0,0,0,0.7)";
  ctx.fillRect(cx - 20, cy, 40, 6);
  ctx.fillStyle = frac > 0.6 ? "#2E9D5E" : frac > 0.3 ? "#F5B71B" : "#E63946";
  ctx.fillRect(cx - 20, cy, 40 * Math.max(0, frac), 6);
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  ctx.strokeRect(cx - 20, cy, 40, 6);
  ctx.restore();
}
